// Image helpers for the funnel's character preview: a Blob from
// /previewCharacterImage (or a ready-made friend's still) becomes the data
// URL the funnel keeps in sessionStorage and shows on its stages.

// The box of pixels that are not (nearly) transparent, with a little air
// around it, or null when the image has no transparent edge to trim — the four
// corners are solid, so it is a picture, not a cut-out.
const CUTOUT_ALPHA_MIN = 24;
export const cutoutBounds = (ctx, width, height) => {
  const { data } = ctx.getImageData(0, 0, width, height);
  const alphaAt = (x, y) => data[(y * width + x) * 4 + 3];
  const corners = [[0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1]];
  if (corners.every(([x, y]) => alphaAt(x, y) > 250)) return null;
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (alphaAt(x, y) < CUTOUT_ALPHA_MIN) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return null; // nothing visible at all
  const pad = Math.round(Math.max(width, height) * 0.02);
  left = Math.max(0, left - pad); top = Math.max(0, top - pad);
  right = Math.min(width - 1, right + pad); bottom = Math.min(height - 1, bottom + pad);
  return { x: left, y: top, w: right - left + 1, h: bottom - top + 1 };
};

// Downscale a Blob to a data URL small enough for sessionStorage. By default
// it is flattened onto white and encoded as JPEG (opaque art, smallest). With
// `alpha` a cut-out keeps its transparency: it is trimmed to the character
// (so its feet sit on the stage's edge, however the model framed it) and
// encoded as PNG — the generated character comes back from
// /previewCharacterImage as a cut-out (background="transparent"), and a JPEG
// would paint its backdrop back in. Should the render come back opaque after
// all, it takes the JPEG path, and the funnel frames it like a picture
// (FunnelScreens' isCutoutImage reads the MIME type).
export const blobToDataURL = (blob, maxDim = 768, { alpha = false } = {}) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let source = img;
      let { width, height } = img;
      if (alpha) {
        const full = document.createElement('canvas');
        full.width = width; full.height = height;
        const fctx = full.getContext('2d');
        fctx.drawImage(img, 0, 0);
        const box = cutoutBounds(fctx, width, height);
        if (!box) {
          // Opaque after all: a picture, flattened like any other
          alpha = false;
        } else {
          const cut = document.createElement('canvas');
          cut.width = box.w; cut.height = box.h;
          cut.getContext('2d').drawImage(full, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
          source = cut; width = box.w; height = box.h;
        }
      }
      const scale = Math.min(1, maxDim / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const ctx = canvas.getContext('2d');
      if (!alpha) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      resolve(alpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

// A character already saved on the account (its public image URL) in the
// same form a freshly generated one takes: fetched, run through the cut-out
// check, and encoded as a PNG data URL when it has transparency (so it stands
// on the stage) or a JPEG when it is a picture (so it is framed). Falls back
// to the URL itself when the image cannot be fetched or read, which the page
// then frames as it always did.
export const characterUrlToPreview = async (url, maxDim = 768) => {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await blobToDataURL(blob, maxDim, { alpha: true });
  } catch (_) {
    return url;
  }
};
